import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './App.css';
import reviewsData from './reviews.json';
import appScreenshot1 from './assets/images/v23-main-page.png';
import appScreenshot2 from './assets/images/v23-audio.png';
import appScreenshot3 from './assets/images/v23-clock.png';
import Navigation from './Navigation';
import LanguageSelector from './LanguageSelector';
import { useAnalytics, usePageTracking } from './hooks/useAnalytics';
import useTranslation from './hooks/useTranslation';

function HomePage() {
  const [showAndroidModal, setShowAndroidModal] = useState(false);
  const [showIosModal, setShowIosModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 8;
  
  // Analytics and translation hooks
  const { trackAppStoreClick, trackModalInteraction, trackFooterLink } = useAnalytics();
  const { t, isGerman, currentLanguage } = useTranslation();
  
  // Helper function to create links with language parameter
  const createLocalizedLink = (path) => {
    if (currentLanguage === 'de') {
      return `${path}${path.includes('?') ? '&' : '?'}lang=de`;
    }
    return path;
  };
  
  // Track page view
  usePageTracking('home', { 
    page_title: t('homepage.title', 'ChronoLog - Your Watch\'s Performance, Perfected')
  });

  const openModal = useCallback((modalType, updateUrl = false) => {
    if (modalType === 'android') {
      setShowAndroidModal(true);
      trackModalInteraction('open', 'android_testing', {
        triggered_by: updateUrl ? 'android_button' : 'url_parameter'
      });
    } else if (modalType === 'ios') {
      setShowIosModal(true);
      trackModalInteraction('open', 'ios_testing', {
        triggered_by: updateUrl ? 'footer_link' : 'url_parameter'
      });
    }

    if (updateUrl) {
      const url = new URL(window.location);
      url.searchParams.set('modal', modalType);
      window.history.pushState({}, '', url);
    }
  }, [trackModalInteraction]);
  
  const closeModal = () => {
    setShowAndroidModal(false);
    setShowIosModal(false);
    
    // Track modal close event (assuming we know which modal was open, could be improved)
    if (showAndroidModal) {
      trackModalInteraction('close', 'android_testing');
    }
    if (showIosModal) {
      trackModalInteraction('close', 'ios_testing');
    }
    
    // Remove modal parameter from URL when closing
    const url = new URL(window.location);
    url.searchParams.delete('modal');
    window.history.replaceState({}, '', url);
  };

  // Shuffle reviews randomly when component mounts
  useEffect(() => {
    const shuffledReviews = [...reviewsData.reviews].sort(() => Math.random() - 0.5);
    setReviews(shuffledReviews);
  }, []);

  // Check URL parameters to open modal automatically
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const modalParam = urlParams.get('modal');
    if (modalParam === 'android') {
      openModal('android');
    } else if (modalParam === 'ios') {
      openModal('ios');
    }
  }, [openModal]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const modalParam = urlParams.get('modal');
      setShowAndroidModal(modalParam === 'android');
      setShowIosModal(modalParam === 'ios');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const totalPages = Math.ceil(reviews.length / reviewsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const currentReviews = reviews.slice((currentPage - 1) * reviewsPerPage, currentPage * reviewsPerPage);

  return (
    <div className="App">
      <Helmet>
        <title>{t('homepage.title', 'ChronoLog - Watch Performance & Accuracy Tracker')}</title>
        <meta name="description" content={t('homepage.metaDescription', 'The ultimate app for watch enthusiasts. Track the accuracy and performance of your mechanical watches, get insights into your collection, and measure precision against atomic time.')} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.chronolog.watch/${isGerman ? '?lang=de' : ''}`} />
        <meta property="og:title" content={t('homepage.ogTitle', 'ChronoLog - Professional Watch Accuracy & Performance Tracker')} />
        <meta property="og:description" content={t('homepage.ogDescription', 'Track mechanical watch accuracy with atomic precision. Professional timegrapher app for collectors, watchmakers, and enthusiasts. Measure daily rate, manage collections, export data.')} />
        <meta property="og:image" content="https://www.chronolog.watch/og-image.png" />
        <meta property="og:site_name" content="ChronoLog" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://www.chronolog.watch/${isGerman ? '?lang=de' : ''}`} />
        <meta property="twitter:title" content={t('homepage.twitterTitle', 'ChronoLog - Professional Watch Accuracy Tracker')} />
        <meta property="twitter:description" content={t('homepage.twitterDescription', 'Track mechanical watch accuracy with atomic precision. Professional timegrapher app for collectors and watchmakers.')} />
        <meta property="twitter:image" content="https://www.chronolog.watch/og-image.png" />
        
        <link rel="canonical" href={`https://www.chronolog.watch/${isGerman ? '?lang=de' : ''}`} />
        <link rel="alternate" hrefLang="en" href="https://www.chronolog.watch/" />
        <link rel="alternate" hrefLang="de" href="https://www.chronolog.watch/?lang=de" />
        <link rel="alternate" hrefLang="x-default" href="https://www.chronolog.watch/" />
        <script type="application/ld+json">

  {`

    {

      "@context": "https://schema.org",

      "@type": "SoftwareApplication",

      "name": "ChronoLog - Watch Accuracy",

      "operatingSystem": "iOS, Android",

      "applicationCategory": "Utilities",

      "publisher": {

        "@type": "Organization",

        "name": "Spine Road Technologies LLC",

        "url": "https://spineroadtechnologiesllc.com"

      },

      "aggregateRating": {

        "@type": "AggregateRating",

        "ratingValue": "4.7",

        "ratingCount": "300"

      },

      "offers": {

        "@type": "Offer",

        "price": "5.99",

        "priceCurrency": "USD"

      }

    }

  `}

</script>
      </Helmet>
      {/* Navigation */}
              <Navigation showAndroidButton={false} onOpenModal={() => openModal('android', true)} />
              
      {/* Language Selector */}
      <div className="language-selector-container">
        <LanguageSelector />
      </div>

      {/* Hero Section with Background Image */}
      <section className="hero">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>

        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">{t('homepage.hero.title', 'ChronoLog')}</h1>
              <h2 className="hero-title_2">{t('homepage.hero.subtitle', 'Your Watch\'s Performance, Perfected')}</h2>

              <p className="hero-subtitle">
                {t('homepage.hero.description', 'Ever wonder if your watch is running fast or slow? ChronoLog measures your timepiece\'s accuracy against atomic time, tracking performance patterns over weeks and months. Get insights into how your watches behave in different situations, spot potential issues before they become problems, and build the ultimate collection with precision data you can trust.')}
                <br/><br/>
                <a href={createLocalizedLink("/info#faq")} className="faq-link">{t('homepage.hero.faqLink', 'Have questions? Check out our FAQ.')}</a>
              </p>

              {/* App Store Badges in Hero */}
              <div className="hero-app-badges">
                <a 
                  href="https://apps.apple.com/us/app/chronolog-watch-accuracy/id6452083510" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="app-store-button ios" 
                  aria-label="Download on the App Store"
                  onClick={() => trackAppStoreClick('ios', { location: 'hero_section' })}
                >
                  <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" />
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=wood.tyler.chronolog"
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="app-store-button android" 
                  aria-label="Get it on Google Play"
                  onClick={() => trackAppStoreClick('android', { location: 'hero_section' })}
                >
                  <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" />
                </a>
              </div>

              {/* Social Proof for App Store */}
              <div className="social-proof-badge">
                <div className="rating-section">
                  <span className="rating">{t('homepage.socialProof.rating', '4.7+')}</span>
                  <span className="stars">★★★★★</span>
                </div>
                <div className="reviews-section">
                  <span>{t('homepage.socialProof.reviews', 'Over 200+ 5 Star Reviews')}</span>
                </div>
              </div>
            </div>
            
            {/* App Screenshots */}
            <div className="hero-screenshots">
              <div className="screenshot-stack">
                <div className="screenshot-item screenshot-left">

                  <img src={appScreenshot2} alt="ChronoLog audio check measuring a mechanical watch's rate in seconds per day." />

                </div>
                <div className="screenshot-item screenshot-center">
                <img src={appScreenshot1} alt="ChronoLog app showing a collection of watches with their measured accuracy." />
                </div>
                <div className="screenshot-item screenshot-right">
                  <img src={appScreenshot3} alt="ChronoLog atomic time clock with timezone and sync status." />
                </div>
              </div>
            </div>
          </div>
          
          {/* German Translation Announcement */}
          <div className="german-translation-banner">
            <div className="german-flag">🇩🇪</div>
            <span className="german-text">
              {t('homepage.germanBanner.text', 'Vollständige deutsche Übersetzung verfügbar • Feedback erwünscht!')}
            </span>
          </div>
        </div>
      </section>

      {/* Reviews Grid Section */}
      <section className="social-proof">
        <div className="container">
          <h2 className="section-title">{t('homepage.reviewsSection.title', 'What Users Say')}</h2>
          <div className="reviews-grid">
            {currentReviews.map((review) => {
              // Format the ISO date to a readable format
              const formatDate = (isoDate) => {
                const date = new Date(isoDate);
                return date.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'short' 
                });
              };

              // Convert stars string to number for star display
              const starCount = Math.floor(parseFloat(review.stars));

              return (
                <div key={review.id} className="review-card">
                  <div className="stars">
                    {'★'.repeat(starCount)}
                  </div>
                  <h3 className="review-title">{review.title}</h3>
                  <p className="review-text">"{review.review}"</p>
                  <div className="reviewer-info">
                    <span className="reviewer-name">{review.author}</span>
                    <span className="review-meta">{formatDate(review.date)} • {review.version}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {reviews.length > reviewsPerPage && (
            <div className="pagination">
              <button onClick={handlePrevPage} disabled={currentPage === 1}>
                {t('homepage.pagination.previous', '← Previous')}
              </button>
              <span className="page-info">
                {t('homepage.pagination.page', 'Page')} {currentPage} of {totalPages}
              </span>
              <button onClick={handleNextPage} disabled={currentPage === totalPages}>
                {t('homepage.pagination.next', 'Next →')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Guides Section */}
      <section className="guides-section">
        <div className="container">
          <h2 className="section-title">{t('homepage.guidesSection.title', 'Watch Accuracy Guides')}</h2>
          <p className="guides-intro">
            {t('homepage.guidesSection.intro', 'How accurate should your watch be — and what can you do when it isn\'t? Practical answers to the questions collectors ask.')}
          </p>
          <div className="guides-grid">
            <Link to={createLocalizedLink('/accuracy')} className="guide-card">
              <h3>{t('homepage.guidesSection.accuracy', 'How accurate should a mechanical watch be?')}</h3>
              <p>{t('homepage.guidesSection.accuracyBlurb', 'Normal daily rates, certification standards, and when a service is due.')}</p>
            </Link>
            <Link to={createLocalizedLink('/measure-watch-accuracy')} className="guide-card">
              <h3>{t('homepage.guidesSection.measure', 'Measure your watch\'s accuracy at home')}</h3>
              <p>{t('homepage.guidesSection.measureBlurb', 'Three ways to get a trustworthy seconds-per-day number — no equipment needed.')}</p>
            </Link>
            <Link to={createLocalizedLink('/watch-running-fast')} className="guide-card">
              <h3>{t('homepage.guidesSection.fast', 'Why is my watch running fast or slow?')}</h3>
              <p>{t('homepage.guidesSection.fastBlurb', 'Magnetism, service wear, or position effects — how to tell which one it is.')}</p>
            </Link>
            <Link to={createLocalizedLink('/what-is-a-timegrapher')} className="guide-card">
              <h3>{t('homepage.guidesSection.timegrapher', 'What is a timegrapher?')}</h3>
              <p>{t('homepage.guidesSection.timegrapherBlurb', 'Rate, amplitude, and beat error explained, with healthy ranges.')}</p>
            </Link>
          </div>
          <div className="guides-all">
            <Link to={createLocalizedLink('/guides')} className="guides-all-link">
              {t('homepage.guidesSection.all', 'Browse all guides →')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-links">
              <a 
                href={createLocalizedLink("/info#privacy")} 
                className="footer-link"
                onClick={() => trackFooterLink('privacy', '/info#privacy')}
              >
                {t('homepage.footer.privacy', 'Privacy Policy')}
              </a>
              <a 
                href="#ios-testing" 
                className="footer-link"
                onClick={(e) => {
                  e.preventDefault();
                  openModal('ios', true);
                  trackFooterLink('ios_testing', '#ios-testing');
                }}
              >
                {t('homepage.footer.iosTestingLink', 'iOS Testing')}
              </a>
              <a 
                href="#android-testing" 
                className="footer-link"
                onClick={(e) => {
                  e.preventDefault();
                  openModal('android', true);
                  trackFooterLink('android_testing', '#android-testing');
                }}
              >
                {t('homepage.footer.androidTestingLink', 'Android Testing')}
              </a>
            </div>
            <div className="social-links">
              <a 
                href="mailto:tylerjaywood+chronologwebsite@gmail.com" 
                className="social-link" 
                aria-label="Email"
                onClick={() => trackFooterLink('email', 'tylerjaywood+chronologwebsite@gmail.com')}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </a>
            </div>
            <p className="copyright">

  © 2026 ChronoLog. Developed and operated by{' '}

  <a

    href="https://spineroadtechnologiesllc.com"

    target="_blank"

    rel="noopener noreferrer"

    className="footer-link"

  >

    Spine Road Technologies LLC

  </a>.

</p>
          </div>
        </div>
      </footer>

      {/* Android Testers Modal */}
      {showAndroidModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('homepage.modals.android.title', 'Android Beta Testing')}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <p className="play-store-note">
                {t('homepage.modals.android.description', 'Join our beta program! Help us test new features and improvements while getting early access to updates.')}
              </p>
              
              <div className="testing-links">
                <a 
                  href="https://groups.google.com/g/chronolog-android-testers" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="testing-link google-group"
                  onClick={() => trackModalInteraction('join_testing_group', 'android_testing', {
                    destination: 'google_groups'
                  })}
                >
                  <strong>{t('homepage.modals.android.joinGroup', '1. Join Testing Group')}</strong>
                  <span>{t('homepage.modals.android.joinGroupDesc', 'Required first step - join our Google Group')}</span>
                </a>
                
                <a 
                  href="https://play.google.com/store/apps/details?id=wood.tyler.chronolog" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="testing-link android-testing"
                  onClick={() => trackModalInteraction('download_beta', 'android_testing', {
                    destination: 'play_store'
                  })}
                >
                  <strong>{t('homepage.modals.android.getBeta', '2. Get Beta App')}</strong>
                  <span>{t('homepage.modals.android.getBetaDesc', 'Download the beta with free premium features')}</span>
                </a>
              </div>

              <div className="testing-benefits">
                {t('homepage.modals.android.benefits', 'Beta Benefits: Free premium unlock • Priority support • Early access to new features')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* iOS Testers Modal */}
      {showIosModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('homepage.modals.ios.title', 'Join iOS Testing')}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <p className="play-store-note">
                {t('homepage.modals.ios.description', 'Help us test new features! Join our TestFlight group to get early access to updates and help us improve the app.')}
              </p>
              
              <div className="testing-links">
                <a 
                  href="https://testflight.apple.com/join/HNVq5Nsi" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="testing-link ios-testing"
                  onClick={() => trackModalInteraction('join_testing_group', 'ios_testing', {
                    destination: 'testflight'
                  })}
                >
                  <strong>{t('homepage.modals.ios.joinTestflight', 'Join TestFlight Beta')}</strong>
                  <span>{t('homepage.modals.ios.joinTestflightDesc', 'Get the beta app and help us with testing')}</span>
                </a>
              </div>

              <div className="testing-benefits">
                {t('homepage.modals.ios.benefits', 'Beta Benefits: Early access to new features • Priority support • Shape the future of the app')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage; 
